/**
 * Thin API client for the WL Editor server.
 *
 * Concurrency: commit() sends the head version the edit was based on.
 * A 409 means someone else committed first — the caller must re-checkout
 * and re-apply, never silently overwrite.
 */

const TOKEN_KEY = 'wl_editor_token';
const AUTOLOGIN_KEY = 'wl_editor_autologin';

/**
 * 자동 로그인 여부. true면 토큰을 localStorage에 담아 브라우저를 닫아도
 * 유지하고, false면 sessionStorage에 담아 탭/브라우저를 닫으면 사라진다.
 * 기본값은 true(기존 동작 유지).
 */
export function getAutoLogin() {
  return localStorage.getItem(AUTOLOGIN_KEY) !== '0';
}

export function setAutoLogin(on) {
  localStorage.setItem(AUTOLOGIN_KEY, on ? '1' : '0');
  // 선호가 바뀌면 이미 저장된 토큰을 알맞은 저장소로 옮긴다.
  const token = getToken();
  if (token) setToken(token);
}

export function getToken() {
  // 어느 쪽에 저장돼 있든 찾는다(선호가 바뀐 직후에도 안전).
  return localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  // 한쪽에만 남지 않도록 항상 양쪽을 비우고 선호한 저장소에만 넣는다.
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  if (token) {
    const store = getAutoLogin() ? localStorage : sessionStorage;
    store.setItem(TOKEN_KEY, token);
  }
}

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }

  /** 428: the account must change its password before anything else works. */
  get mustChangePassword() {
    return this.status === 428 && this.body?.mustChangePassword === true;
  }

  /** 403 from the approval gate, as opposed to an ordinary permission error. */
  get accountBlocked() {
    return this.status === 403 && (this.body?.status === 'pending' || this.body?.status === 'disabled');
  }
}

async function request(path, { method = 'GET', body, headers = {} } = {}) {
  const token = getToken();
  const res = await fetch(path, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const payload = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload;
}

/**
 * Fetch an attachment with auth and hand it to the browser as a download.
 *
 * Errors come back as JSON even though success is binary, so failures are
 * decoded rather than silently saved as a broken file.
 *
 * @returns {Promise<{filename: string, size: number, skipped: string|null}>}
 */
async function downloadFile(path) {
  const token = getToken();
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ApiError(res.status, payload);
  }

  // Prefer the server's filename; fall back to the last path segment.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match ? match[1] : path.split('/').pop();

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  const skipped = res.headers.get('X-Export-Skipped');
  return { filename, size: blob.size, skipped: skipped && skipped !== 'none' ? skipped : null };
}

export const api = {
  register: (email, password, displayName) =>
    request('/api/auth/register', { method: 'POST', body: { email, password, displayName } }),

  async login(email, password) {
    const res = await request('/api/auth/login', { method: 'POST', body: { email, password } });
    setToken(res.token);
    return res.user;
  },

  logout: () => setToken(null),
  me: () => request('/api/auth/me'),

  // 자동 로그인 선호 (토큰을 브라우저 세션 넘어 유지할지 여부).
  getAutoLogin,
  setAutoLogin,

  changePassword: (currentPassword, newPassword) =>
    request('/api/auth/password', { method: 'POST', body: { currentPassword, newPassword } }),

  // ── Admin ────────────────────────────────────────────────────────────
  listUsers: () => request('/api/admin/users'),
  createUser: (email, displayName, role = 'user') =>
    request('/api/admin/users', { method: 'POST', body: { email, displayName, role } }),
  setUserStatus: (id, status) =>
    request(`/api/admin/users/${id}/status`, { method: 'PUT', body: { status } }),
  setUserRole: (id, role) =>
    request(`/api/admin/users/${id}/role`, { method: 'PUT', body: { role } }),
  resetUserPassword: (id) =>
    request(`/api/admin/users/${id}/reset-password`, { method: 'POST' }),
  deleteUser: (id) => request(`/api/admin/users/${id}`, { method: 'DELETE' }),

  // ── Event definitions ────────────────────────────────────────────────
  getEventConfig: () => request('/api/event-config'),
  getEventConfigDefaults: () => request('/api/event-config/defaults'),
  saveEventConfig: (config) => request('/api/event-config', { method: 'PUT', body: { config } }),
  resetEventConfig: () => request('/api/event-config', { method: 'DELETE' }),

  listCharts: () => request('/api/charts'),
  createChart: (musicName, path) =>
    request('/api/charts', { method: 'POST', body: { musicName, path } }),
  getChart: (id) => request(`/api/charts/${id}`),
  listVersions: (id) => request(`/api/charts/${id}/versions`),
  getVersion: (id, v) => request(`/api/charts/${id}/versions/${v}`),

  /** @throws {ApiError} 409 when HEAD moved, 422 when contract validation failed. */
  commit: (id, { chart, message, expectedHeadVersionId }) =>
    request(`/api/charts/${id}/commit`, {
      method: 'POST',
      body: { chart, message, expectedHeadVersionId },
    }),

  async uploadAudio(file) {
    const form = new FormData();
    form.append('file', file);
    const token = getToken();
    const res = await fetch('/api/audio', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, payload);
    return payload;
  },

  // ── File browser ─────────────────────────────────────────────────────
  listFiles: (path = 'svn') => request(`/api/files?path=${encodeURIComponent(path)}`),
  createFolder: (path) => request('/api/files/folder', { method: 'POST', body: { path } }),
  moveFile: (from, to) => request('/api/files/move', { method: 'POST', body: { from, to } }),
  moveFolder: (from, to) => request('/api/files/move-folder', { method: 'POST', body: { from, to } }),
  copyFile: (from, to) => request('/api/files/copy', { method: 'POST', body: { from, to } }),
  deleteFile: (path, recursive = false) =>
    request('/api/files', { method: 'DELETE', body: { path, recursive } }),

  // ── SVN ──────────────────────────────────────────────────────────────
  svnStatus: () => request('/api/svn/status'),
  svnLog: (path, limit = 50) =>
    request(`/api/svn/log?path=${encodeURIComponent(path)}&limit=${limit}`),
  svnImport: (opts = {}) => request('/api/svn/import', { method: 'POST', body: opts }),
  /** 로컬 HEAD와 저장소 현재 내용을 비교한다. status: identical|local-only|ahead|conflict */
  /** 저장소가 우리보다 앞선 차트 목록 — 읽기 전용, 아무것도 가져오지 않는다. */
  svnStale: () => request('/api/svn/stale'),
  svnDiff: (path) => request(`/api/svn/diff?path=${encodeURIComponent(path)}`),
  /** 저장소 내용을 에디터로 되돌린다(publish의 역방향). */
  svnUpdate: (path) => request('/api/svn/update', { method: 'POST', body: { path } }),
  /** 경합이 아닌 것만 일괄로 가져온다. dryRun 이면 대상만 세어 본다. */
  svnUpdateAll: (dryRun = false) =>
    request('/api/svn/update-all', { method: 'POST', body: { dryRun } }),
  /** force=true 는 경합 확인 패널에서 사용자가 덮어쓰기를 고른 경우에만. */
  svnPublish: (path, message, force = false) =>
    request('/api/svn/publish', { method: 'POST', body: { path, message, force } }),
  svnPublishFolder: (path, message, force = false) =>
    request('/api/svn/publish-folder', { method: 'POST', body: { path, message, force } }),
  svnPublishAll: (message, dryRun = false, force = false) =>
    request('/api/svn/publish-all', { method: 'POST', body: { message, dryRun, force } }),

  // ── Tracks & difficulties ────────────────────────────────────────────
  listTracks: (mine = false) => request(`/api/tracks${mine ? '?mine=1' : ''}`),
  setTrackAssignee: (id, assigneeId) =>
    request(`/api/tracks/${id}/assignee`, { method: 'PUT', body: { assigneeId } }),
  createTrack: (code, name, audioId = null) =>
    request('/api/tracks', { method: 'POST', body: { code, name, audioId } }),
  getTrack: (id) => request(`/api/tracks/${id}`),

  /** Partial update: pass only the fields to change. audioId null detaches. */
  updateTrack: (id, changes) =>
    request(`/api/tracks/${id}`, { method: 'PATCH', body: changes }),

  /**
   * Place a chart as the track's pattern for a difficulty: moves it to the
   * canonical svn/<TrackDir>/<Code>-<DIFF>.json path (auto-renaming the file).
   * This is the work/ -> svn/ transfer.
   */
  placePattern: (trackId, difficulty, chartId, backup = false) =>
    request(`/api/tracks/${trackId}/difficulties/${difficulty}/place`, {
      method: 'PUT', body: { chartId, backup },
    }),

  // ── Export ───────────────────────────────────────────────────────────
  // These return files, not JSON, so they bypass request() and stream to a
  // download. The JWT goes in the header, so the browser cannot simply follow
  // a link -- fetch + object URL is the way.
  exportChart: (id) => downloadFile(`/api/charts/${id}/export`),
  exportTrack: (id) => downloadFile(`/api/tracks/${id}/export`),
  exportAllTracks: () => downloadFile('/api/tracks/export'),

  /**
   * Resolve a playable URL for <audio src>.
   *
   * <audio> cannot send an Authorization header, so we trade the JWT for a
   * short-lived signed URL (HMAC over sha256|expiry, ~1h). The stream route
   * still rejects unauthenticated requests — the signature replaces the header,
   * it does not remove the check. The signature is bound to this sha256, so it
   * cannot be replayed against another file.
   *
   * Returns a URL that expires; call again for a fresh one rather than caching
   * it across a long session.
   */
  async audioUrl(sha256) {
    const { url } = await request(`/api/audio/${sha256}/url`, { method: 'POST' });
    return url;
  },
};
